
import { BASE_ACCOUNT_API, fetchAPI } from '@/services/api';

/**
 * 公司级 GL 类别记录（双重职责）：
 * - 公司级 GL 科目
 * - 公司级列名别名映射（旧格式 Excel 与 Co. GL Cate 页面共用）
 *
 * 字段变更（2026-04）：原 `glCode` 已合并到 `code`。
 * 参考：docs/account-company-gl-category-requirements.md §6.1.1
 */
export interface GlAccount {
  accountId?: number;
  companyId?: number;
  /** 对应 account_gl_category.code（原 glCode，已统一） */
  code?: string;
  aliasEn?: string;
  aliasZh?: string;
  normalSide?: 'DR' | 'CR';
  currency?: string;
  isActive?: number;
  isDel?: number;
}

/** 变更类型（见后端 GlCategoryHistoryConst.ChangeType） */
export type GlAccountChangeType = 'UPDATE' | 'DELETE';

/** 变更来源（见后端 GlCategoryHistoryConst.ChangeReason） */
export type GlAccountChangeReason = 'LEGACY_CONFIRM' | 'NEW_FORMAT_SYNC' | 'MANUAL_CRUD';

/** 操作入口（见后端 GlCategoryHistoryConst.OperationSource） */
export type GlAccountOperationSource = 'EXCEL_UPLOAD' | 'WEB_CRUD' | 'API' | 'MIGRATION';

/** 公司 GL 别名变更历史快照 */
export interface GlAccountHistory {
  historyId: number;
  accountId: number;
  companyId: number;
  code: string;
  aliasEn?: string;
  aliasZh?: string;
  normalSide?: string;
  currency?: string;
  changeType: GlAccountChangeType;
  changeReason?: GlAccountChangeReason;
  changeTime: string;
  changeUserId?: number;
  operationIp?: string;
  operationSource?: GlAccountOperationSource;
}

const base = BASE_ACCOUNT_API + '/company-gl-category';

/** GET /company-gl-category/get?id={id} — 单条详情 */
export async function getAccount(id: number) {
  return fetchAPI<GlAccount>(`${base}/get?id=${id}`);
}

/** GET /company-gl-category/list — 全量未删除列表（仅内部/兼容；页面请用 listAccountsByCompany） */
export async function listAccounts() {
  return fetchAPI<GlAccount[]>(`${base}/list`);
}

/** GET /company-gl-category/list-by-company?companyId= — 按公司拉取别名映射 */
export async function listAccountsByCompany(companyId: number) {
  return fetchAPI<GlAccount[]>(`${base}/list-by-company?companyId=${companyId}`);
}

/** POST /company-gl-category/create */
export async function createAccount(vo: GlAccount) {
  return fetchAPI<GlAccount>(`${base}/create`, {
    method: 'POST',
    body: JSON.stringify(vo),
  });
}

/** POST /company-gl-category/update/{id} */
export async function updateAccount(id: number, vo: GlAccount) {
  return fetchAPI<GlAccount>(`${base}/update/${id}`, {
    method: 'POST',
    body: JSON.stringify(vo),
  });
}

/** POST /company-gl-category/delete/{id} — 逻辑删除 */
export async function deleteAccount(id: number) {
  return fetchAPI<void>(`${base}/delete/${id}`, { method: 'POST' });
}

/**
 * GET /company-gl-category/history?companyId={}&accountId={} — 变更历史
 * accountId 可选；不传则返回该公司的所有变更记录，按 changeTime DESC 排序。
 */
export async function listHistory(params: { companyId: number; accountId?: number }) {
  const qs: string[] = [`companyId=${params.companyId}`];
  if (params.accountId != null) qs.push(`accountId=${params.accountId}`);
  return fetchAPI<GlAccountHistory[]>(`${base}/history?${qs.join('&')}`);
}

export interface GlAccountBatchUpsertItem {
  code: string;
  aliasEn: string;
  aliasZh?: string;
  normalSide?: string;
  currency?: string;
}

export interface GlAccountBatchUpsertResult {
  created: number;
  updated: number;
  skipped: number;
  failed: number;
  messages?: string[];
}

/** POST /company-gl-category/batch-upsert — 按 code 批量 upsert */
export async function batchUpsertByCode(companyId: number, items: GlAccountBatchUpsertItem[]) {
  return fetchAPI<GlAccountBatchUpsertResult>(`${base}/batch-upsert`, {
    method: 'POST',
    body: JSON.stringify({companyId, items}),
  });
}

